import React from "react";
import { Formik, Form, Field } from "formik";
import * as Yup from "yup";
import { useDispatch } from "react-redux";

import Dialog from "@material-ui/core/Dialog";
import DialogActions from "@material-ui/core/DialogActions";
import DialogContent from "@material-ui/core/DialogContent";
import DialogTitle from "@material-ui/core/DialogTitle";
import Typography from "@material-ui/core/Typography";
import { makeStyles } from "@material-ui/core/styles";

import history from "../../store/history";
import {
  ADM_AREAS_CREATE_URL,
  ADM_AREAS_EDIT_URL,
  REGEX_URL_HTTPS,
} from "../../config";
import SubmitButton from "../button/SubmitButton";
import CancelButton from "../button/CancelButton";
import {
  showSuccessAlert,
  showErrorAlert,
  showWarningAlert,
} from "../../store/actions/alerts";

import TextField from "../formik/TextField";
import Select from "../formik/Select";
import CKEditor4 from "../formik/CKEditor4";

import makeApiRequest from "../../utils/axios/new-axios";

const useStyles = makeStyles((theme) => ({
  form: {
    display: "flex",
    flexDirection: "column",
    margin: "auto",
    width: "fit-content",
  },
}));

const yupSchema = Yup.object().shape({
  name: Yup.string().trim().required("Name Required!"),
  nameAlt: Yup.string().trim().required("Short Name Required!"),
  nameLocal: Yup.string().trim().nullable(),
  lat: Yup.number("Latitude must be between -90 & 90!")
    .min(-90, "Latitude must be between -90 & 90!")
    .max(90, "Latitude must be between -90 & 90!")
    .required("Latitude Required!"),
  lng: Yup.number("Longitude must be between -180 & 180!")
    .min(-180, "Longitude must be between -180 & 180!")
    .max(180, "Longitude must be between -180 & 180!")
    .required("Longitude Required!"),
  caption: Yup.string().trim().nullable(),
  description: Yup.string().trim().nullable(),
  link: Yup.string()
    .trim()
    // .url("Must be an URL")
    .matches(REGEX_URL_HTTPS, "Must be a URL (https)")
    .nullable(),
  meta: Yup.object().shape({
    title: Yup.string().trim().max(500, "Cannot be more than 500 characters!").nullable(),
    description: Yup.string().trim().nullable(),
  }),
  googlePlaceId: Yup.string().trim().required("Google Place ID Required!"),
  hasChild: Yup.number().min(0).max(1).integer().required(),
  status: Yup.number().min(0).max(1).integer().required(),
});

export default function AddEditAdmForm({
  action,
  level,
  openModal,
  handleCloseModal,
  initialValues,
  admId,
  setValues,
  setMapdetails,
  setHasChild,
}) {
  const dispatch = useDispatch();
  const classes = useStyles();
  //const [disableModalDismiss, setDisableModalDismiss] = useState(false);

  async function handleSubmit(values, { setSubmitting }) {
    setSubmitting(true);
    //setDisableModalDismiss(true);
    const reqJson = {
      ...values,
      hasChild: level <= 2 ? values.hasChild : 0,
      name: values.name.trim(),
      nameAlt: values.nameAlt.trim().length > 0 ? values.nameAlt.trim() : null,
      nameLocal:
        values.nameLocal.trim().length > 0 ? values.nameLocal.trim() : null,
      caption: values.caption.trim().length > 0 ? values.caption.trim().replace(/\u00A0/g, " ") : null,
      description:
        values.description.trim().length > 0 ? values.description.trim() : null,
      link: values.link.trim().length > 0 ? values.link.trim() : null,
      meta: {
        title: values.meta.title.trim().length > 0 ? values.meta.title.trim() : null,
        description: values.meta.description.trim().length > 0 ? values.meta.description.trim() : null,
      },
      googlePlaceId:
        values.googlePlaceId.trim().length > 0
          ? values.googlePlaceId.trim()
          : null,
    };

    //remove the null keys
    // Object.keys(reqJson).forEach(
    //   key => reqJson[key] == null && delete reqJson[key]
    // );
    await makeApiRequest({
      url:
        action === "add"
          ? ADM_AREAS_CREATE_URL.url
          : `${ADM_AREAS_EDIT_URL.url}/${admId}`,
      method:
        action === "add"
          ? ADM_AREAS_CREATE_URL.method
          : ADM_AREAS_EDIT_URL.method,
      data: reqJson,
    })
      .then((response) => {
        if (action === "add") {
          dispatch(showSuccessAlert(`Created ${reqJson.name} successfully`));
          //redirect
          history.push(`/adm-areas/${response.data.admId}`);
        } else {
          //show alert
          if (response.data.affectedRows > 0) {
            dispatch(showSuccessAlert(`Updated Successfully!`));
            //update the details in the page (state change)
            setValues({
              ...values,
              hasChild: level <= 2 ? values.hasChild : 0,
            });
            setMapdetails({
              coordinates: {
                lat: Number(reqJson.lat),
                lng: Number(reqJson.lng),
              },
              infoWindowTitle: reqJson.name,
              infoWindowDetail: reqJson.googlePlaceId,
            });
            setHasChild(reqJson.hasChild);
          } else {
            dispatch(showWarningAlert(`Nothing updated. But no errors!`));
          }
          handleCloseModal();
        }
      })
      .catch((error) => {
        setSubmitting(false);
        //setDisableModalDismiss(false);
        dispatch(
          showErrorAlert(
            "Error : " +
            (!!error.response
              ? `${error.response.status}! - ${error.response.data.errorMessage}`
              : error.message)
          )
        );
      });
  }

  return (
    <Dialog
      open={openModal}
      onClose={handleCloseModal}
      fullWidth={true}
      maxWidth="sm"
      aria-labelledby="adm-dialog-add-edit"
      aria-describedby="adm-dialog-add-edit"
      disableBackdropClick={true}
      disableEscapeKeyDown={true}
      disableEnforceFocus={true}
    >
      <DialogTitle id="adm-dialog-title">
        {action === "add" ? "Add Sub" : "Edit"} Administrative Area
      </DialogTitle>
      <Formik
        initialValues={initialValues}
        onSubmit={handleSubmit}
        validationSchema={yupSchema}
      >
        {(props) => {
          const { touched, errors, isSubmitting } = props;
          return (
            <Form className={classes.form}>
              <DialogContent dividers style={{ overflow: "visible" }}>
                <Field
                  component={TextField}
                  name="name"
                  margin="dense"
                  label="Name *"
                  fullWidth
                  helperText={touched.name && errors.name && errors.name}
                  error={!!touched.name && !!errors.name}
                  disabled={isSubmitting}
                  autoFocus
                />
                <Field
                  component={TextField}
                  name="nameAlt"
                  margin="dense"
                  label="Short Name"
                  fullWidth
                  helperText={
                    touched.nameAlt && errors.nameAlt && errors.nameAlt
                  }
                  error={!!touched.nameAlt && !!errors.nameAlt}
                  disabled={isSubmitting}
                />
                <Field
                  component={TextField}
                  name="nameLocal"
                  margin="dense"
                  label="Local Name"
                  fullWidth
                  helperText={
                    touched.nameLocal && errors.nameLocal && errors.nameLocal
                  }
                  error={!!touched.nameLocal && !!errors.nameLocal}
                  disabled={isSubmitting}
                />
                <Field
                  component={TextField}
                  name="lat"
                  margin="dense"
                  label="Latitude *"
                  fullWidth
                  helperText={touched.lat && errors.lat && errors.lat}
                  error={!!touched.lat && !!errors.lat}
                  disabled={isSubmitting}
                />
                <Field
                  component={TextField}
                  name="lng"
                  margin="dense"
                  label="Longitude *"
                  fullWidth
                  helperText={touched.lng && errors.lng && errors.lng}
                  error={!!touched.lng && !!errors.lng}
                  disabled={isSubmitting}
                />
                <Field
                  component={TextField}
                  name="caption"
                  margin="dense"
                  label="Short Description"
                  fullWidth
                  multiline
                  maxRows={5}
                  helperText={
                    touched.caption && errors.caption && errors.caption
                  }
                  error={!!touched.caption && !!errors.caption}
                  disabled={isSubmitting}
                />
                <Field
                  component={TextField}
                  name="link"
                  margin="dense"
                  label="External Link"
                  fullWidth
                  helperText={touched.link && errors.link && errors.link}
                  error={!!touched.link && !!errors.link}
                  disabled={isSubmitting}
                />
                <Field
                  component={TextField}
                  name="googlePlaceId"
                  margin="dense"
                  label="Google Place ID"
                  fullWidth
                  helperText={
                    touched.googlePlaceId &&
                    errors.googlePlaceId &&
                    errors.googlePlaceId
                  }
                  error={!!touched.googlePlaceId && !!errors.googlePlaceId}
                  disabled={isSubmitting}
                />
                <Field
                  component={Select}
                  name="hasChild"
                  label="Has sub administrative level?"
                  fullWidth
                  options={[
                    {
                      value: 0,
                      label: "No",
                    },
                    {
                      value: 1,
                      label: "Yes",
                    },
                  ]}
                  disabled={
                    isSubmitting || (action === "add" && level > 1) || level > 2
                  }
                />
                <Field
                  component={Select}
                  name="status"
                  label="Status"
                  fullWidth
                  options={action === "add" ?
                    [{
                      value: 0,
                      label: "In-Active",
                    }]
                    : [
                      {
                        value: 0,
                        label: "In-Active",
                      },
                      {
                        value: 1,
                        label: "Active",
                      },
                    ]}
                  disabled={isSubmitting}
                />
                <h3 style={{ color: "red", backgroundColor: "yellow", textAlign: "center" }}>
                  Add / Update Meta Data only if you are sure about it!
                </h3>
                <Field
                  component={TextField}
                  name="meta.title"
                  margin="dense"
                  label="Meta Title"
                  fullWidth
                  helperText={touched?.meta?.title && errors?.meta?.title && errors?.meta?.title}
                  error={!!touched?.meta?.title && !!errors?.meta?.title}
                  disabled={isSubmitting}
                />
                <Field
                  component={TextField}
                  name="meta.description"
                  margin="dense"
                  label="Meta Description"
                  fullWidth
                  helperText={touched?.meta?.description && errors?.meta?.description && errors?.meta?.description}
                  error={!!touched?.meta?.description && !!errors?.meta?.description}
                  disabled={isSubmitting}
                />
                <Typography
                  variant="caption"
                  display="block"
                  style={{ marginTop: "15px" }}
                  gutterBottom
                >
                  Description
                </Typography>
                <Field
                  component={CKEditor4}
                  name="description"
                  disabled={isSubmitting}
                />
              </DialogContent>
              <DialogActions style={{ margin: "20px" }}>
                <CancelButton
                  onClick={handleCloseModal}
                  disabled={isSubmitting}
                >
                  Cancel
                </CancelButton>
                <SubmitButton
                  disabled={isSubmitting}
                  isSubmitting={isSubmitting}
                  autoFocus
                >
                  {action === "add" ? "Add" : "Update"}
                </SubmitButton>
              </DialogActions>
            </Form>
          );
        }}
      </Formik>
    </Dialog>
  );
}
